import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import { exportTable } from "../../../utils/exportTable";
import { btn } from "../../../components/Button/buttonStyles";

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "0.00";

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-IN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const linkedInvoices = (p) => (p.invoiceLinks || []).map((l) => l.invoice).filter(Boolean);

const COVERAGE_CLS = {
  "Fully covered": "text-green-600 font-medium",
  "Partially covered": "text-amber-600 font-medium",
  "Not covered": "text-danger-500 font-medium",
};

const HEADERS = [
  "Code",
  "Linked invoices",
  "Invoice coverage",
  "Total amount",
  "Linked amount",
  "Unlinked amount",
  "Currency",
  "Payment method",
  "Paid at",
  "Note",
];

// Mirrors HEADERS above, in the same order.
const EXPORT_COLUMNS = [
  { header: "Code", value: (p) => p.payment_code || "" },
  { header: "Linked invoices", value: (p) => linkedInvoices(p).map((i) => i.invoice_code).join(", ") },
  { header: "Invoice coverage", value: (p) => p.coverage_by_invoices || "" },
  { header: "Total amount", value: (p) => fmtNum(p.amount) },
  { header: "Linked amount", value: (p) => fmtNum(p.linked_amount) },
  { header: "Unlinked amount", value: (p) => fmtNum(p.unlinked_amount) },
  { header: "Currency", value: (p) => p.currency || "" },
  {
    header: "Payment method",
    value: (p) => p.paymentMethod?.payment_method?.replace(/_/g, " ") || "",
  },
  { header: "Paid at", value: (p) => fmtDate(p.paid_at) },
  { header: "Note", value: (p) => p.note || "" },
];

const VendorPaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/vendor-payments", { withCredentials: true })
      .then(({ data }) => setPayments(data.data || []))
      .catch(() => setError("Failed to load payments"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Vendor Payments</h1>
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => exportTable("my-payments", EXPORT_COLUMNS, payments)}
            className={btn("secondary", "sm")}
          >
            Export to Excel
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
        </div>
      )}
      {error && <p className="text-danger-600 text-sm">{error}</p>}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{payments.length}</span>{" "}
            items.
          </p>

          <div className="w-full bg-white shadow rounded-lg overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-auto border-collapse min-w-[1100px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    {HEADERS.map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr>
                      <td
                        colSpan={HEADERS.length}
                        className="py-10 text-center text-gray-400 text-xs"
                      >
                        No payments found.
                      </td>
                    </tr>
                  ) : (
                    payments.map((p, index) => {
                      const pmType =
                        p.paymentMethod?.payment_method?.replace(/_/g, " ") ||
                        "—";
                      const coverageCls =
                        COVERAGE_CLS[p.coverage_by_invoices] ?? "text-gray-600";
                      const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";

                      return (
                        <tr
                          key={p.id}
                          className={`${rowBg} hover:bg-brand-50 transition-colors`}
                        >
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            <Link
                              to={`/vendor/payment/${p.id}`}
                              className="text-brand-600 hover:text-brand-800 underline font-medium"
                            >
                              {p.payment_code}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-xs text-center">
                            {linkedInvoices(p).length ? (
                              <div className="flex flex-wrap justify-center gap-x-2">
                                {linkedInvoices(p).map((inv) => (
                                  <Link
                                    key={inv.id}
                                    to={`/vendor/invoice/${inv.id}`}
                                    className="text-brand-600 hover:underline font-medium"
                                  >
                                    {inv.invoice_code}
                                  </Link>
                                ))}
                              </div>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td
                            className={`px-3 py-2 text-xs text-center ${coverageCls}`}
                          >
                            {p.coverage_by_invoices || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs font-semibold text-right whitespace-nowrap">
                            {fmtNum(p.amount)}
                          </td>
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">
                            {fmtNum(p.linked_amount)}
                          </td>
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">
                            {fmtNum(p.unlinked_amount)}
                          </td>
                          <td className="px-3 py-2 text-xs text-center">
                            {p.currency || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs capitalize text-center">
                            {pmType}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                            {fmtDate(p.paid_at)}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-700">
                            {p.note || "—"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VendorPaymentsPage;
